import { useMemo, useState } from 'react';

import type { EntradaDoCadastro, LinhaDoCadastro } from './cadastros.contrato';
import type { CampoDeFormulario, DefinicaoDoCadastro } from './cadastros.definicoes';

/** Valor de cada campo como a pessoa o digita: texto. Caixa de marcação vale `sim` ou vazio. */
export type ValoresDoFormulario = Record<string, string>;

const COR = /^#?[0-9a-fA-F]{6}$/;
const EMAIL = /^\S+@\S+\.\S+$/;

/** Valores com que o formulário abre: os do registro, ou os padrões de um cadastro novo. */
export function valoresIniciais(
  definicao: DefinicaoDoCadastro,
  registro?: LinhaDoCadastro,
): ValoresDoFormulario {
  const valores: ValoresDoFormulario = {};
  for (const campo of definicao.campos) {
    if (campo.tipo === 'checkbox') {
      // `ativo` é a situação: um cadastro novo nasce ativo.
      const marcado =
        campo.nome === 'ativo'
          ? registro
            ? registro.situacao === 'ativo'
            : true
          : registro?.[campo.nome] === true;
      valores[campo.nome] = marcado ? 'sim' : '';
    } else if (campo.tipo === 'grafias') {
      const lista = registro?.[campo.nome];
      valores[campo.nome] = Array.isArray(lista) ? lista.join(QUEBRA_DE_LINHA) : '';
    } else {
      const valor = registro?.[campo.nome];
      valores[campo.nome] = valor === null || valor === undefined ? '' : String(valor);
    }
  }
  return valores;
}

/** Uma grafia por linha no campo de texto (o retorno de carro do Windows sai no trim). */
const QUEBRA_DE_LINHA = String.fromCharCode(10);

/** Quantas grafias um item aceita (o mesmo limite da API). */
export const MAXIMO_DE_GRAFIAS = 50;

/** Grafias digitadas, uma por linha, sem linha vazia. */
export function grafiasDigitadas(texto: string): string[] {
  return texto
    .split(QUEBRA_DE_LINHA)
    .map((g) => g.trim().replace(/\s+/g, ' '))
    .filter(Boolean);
}

/** Problema do campo, em português, ou `null` quando está certo. */
export function problemaDoCampo(campo: CampoDeFormulario, bruto: string): string | null {
  const valor = bruto.trim();
  if (campo.tipo === 'checkbox') return null;
  if (campo.tipo === 'grafias') {
    const lista = grafiasDigitadas(bruto);
    if (lista.length > MAXIMO_DE_GRAFIAS) {
      return `Informe no máximo ${MAXIMO_DE_GRAFIAS} grafias.`;
    }
    const longa = lista.find((g) => g.length > (campo.maxLength ?? 120));
    return longa
      ? `Cada grafia aceita no máximo ${campo.maxLength ?? 120} caracteres: "${longa.slice(0, 20)}…".`
      : null;
  }

  if (valor === '') return campo.obrigatorio ? `${campo.rotulo} é obrigatório.` : null;

  if (campo.tipo === 'number') {
    const numero = Number(valor);
    if (!/^\d+$/.test(valor) || !Number.isInteger(numero)) {
      return `${campo.rotulo} deve ser um número inteiro.`;
    }
    const min = campo.min ?? 0;
    const max = campo.max ?? Number.MAX_SAFE_INTEGER;
    if (numero < min || numero > max) return `${campo.rotulo} deve estar entre ${min} e ${max}.`;
    return null;
  }
  if (campo.nome === 'cor' && !COR.test(valor)) {
    return `${campo.rotulo} deve estar no formato #RRGGBB.`;
  }
  if (campo.nome === 'cnpj' && valor.replace(/\D/g, '').length !== 14) {
    return 'Informe o CNPJ com os 14 dígitos, ou deixe em branco.';
  }
  if (campo.tipo === 'email' && !EMAIL.test(valor)) {
    return 'Informe um e-mail válido, como nome@orgao.sp.gov.br.';
  }
  if (campo.maxLength !== undefined && valor.length > campo.maxLength) {
    return `${campo.rotulo} aceita no máximo ${campo.maxLength} caracteres.`;
  }
  return null;
}

/** Conferência de todos os campos, e a regra que olha dois campos juntos. */
export function problemasDoFormulario(
  definicao: DefinicaoDoCadastro,
  valores: ValoresDoFormulario,
): Record<string, string> {
  const problemas: Record<string, string> = {};
  for (const campo of definicao.campos) {
    const problema = problemaDoCampo(campo, valores[campo.nome] ?? '');
    if (problema) problemas[campo.nome] = problema;
  }
  if (definicao.chave === 'tipos-veiculo' && !problemas.capacidadeMin && !problemas.capacidadeMax) {
    const min = valores.capacidadeMin?.trim();
    const max = valores.capacidadeMax?.trim();
    if (min && max && Number(max) < Number(min)) {
      problemas.capacidadeMax = 'Lugares (máximo) não pode ser menor que Lugares (mínimo).';
    }
  }
  return problemas;
}

/** Corpo que a API espera: número vira número, marcação vira booleano, vazio vira `null`. */
export function montarEntrada(
  definicao: DefinicaoDoCadastro,
  valores: ValoresDoFormulario,
  registro?: LinhaDoCadastro,
): EntradaDoCadastro {
  const entrada: EntradaDoCadastro = {};
  for (const campo of definicao.campos) {
    const valor = (valores[campo.nome] ?? '').trim();
    if (campo.tipo === 'checkbox') entrada[campo.nome] = valores[campo.nome] === 'sim';
    else if (campo.tipo === 'grafias')
      entrada[campo.nome] = grafiasDigitadas(valores[campo.nome] ?? '');
    else if (campo.tipo === 'number') entrada[campo.nome] = valor === '' ? null : Number(valor);
    else entrada[campo.nome] = valor === '' ? null : valor;
  }
  if (registro?.versao) entrada.versao = registro.versao;
  return entrada;
}

/**
 * Estado de um formulário de cadastro: valores, problemas por campo e a
 * conferência antes de enviar. Quem usa decide onde ele aparece (página ou
 * janela) e o que fazer com a resposta.
 */
export function useFormularioDoCadastro(
  definicao: DefinicaoDoCadastro,
  registro?: LinhaDoCadastro,
) {
  const iniciais = useMemo(() => valoresIniciais(definicao, registro), [definicao, registro]);
  const [valores, setValores] = useState<ValoresDoFormulario>(iniciais);
  const [problemas, setProblemas] = useState<Record<string, string>>({});

  function alterar(nome: string, valor: string) {
    setValores((atuais) => ({ ...atuais, [nome]: valor }));
    setProblemas((atuais) => {
      if (!atuais[nome]) return atuais;
      const restantes = { ...atuais };
      delete restantes[nome];
      return restantes;
    });
  }

  /** Confere o preenchimento; devolve o corpo, ou `null` e põe o foco no primeiro campo errado. */
  function conferir(prefixoDosIds: string): EntradaDoCadastro | null {
    const achados = problemasDoFormulario(definicao, valores);
    setProblemas(achados);
    const primeiro = definicao.campos.find((campo) => achados[campo.nome]);
    if (primeiro) {
      document.getElementById(`${prefixoDosIds}-${primeiro.nome}`)?.focus();
      return null;
    }
    return montarEntrada(definicao, valores, registro);
  }

  return { valores, problemas, alterar, conferir };
}
